import { FC, useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useAppDispatch } from 'hooks/redux';
import HalfScreenModal from 'components/HalfScreenModal/HalfScreenModal';
import RichTextEditor from 'components/RichTextEditor/RichTextEditor';
import { updateCustomPageRichMediaContentAction } from 'modules/customPages/slices/customPagesSlice';

import styles from './textEditor.module.less';

interface TextEditorProps {
  isOpenModal: boolean;
  closeModal: () => void;
  content: string;
  benguideId: string;
  onSave: (content: string) => void;
  editorKey: string;
  customPageId: string;
  customPageSectionId: string;
  componentType?: 'rich_media' | 'content_card';
  title?: string;
}

const TextEditor: FC<TextEditorProps> = ({
  isOpenModal,
  closeModal,
  content,
  benguideId,
  onSave,
  editorKey,
  customPageId,
  customPageSectionId,
  componentType = 'rich_media',
  title = '',
}) => {
  const dispatch = useAppDispatch();
  const [currentContent, setCurrentContent] = useState(content);
  const textEditorRef = useRef<any>(null);

  useEffect(() => {
    setCurrentContent(content);
  }, [content]);

  const handleSubmit = async () => {
    try {
      await dispatch(
        updateCustomPageRichMediaContentAction(
          benguideId,
          customPageId,
          customPageSectionId,
          currentContent,
          componentType === 'content_card' ? title : '',
          componentType === 'content_card' ? 'CONTENT_CARD' : 'RICH_MEDIA',
          () => {
            // Success callback
            onSave(currentContent);
            closeModal();
          }
        )
      );
    } catch (error) {
      console.error('Failed to update rich media content:', error);
      onSave(currentContent);
      closeModal();
    }
  };

  const handleCancel = () => {
    setCurrentContent(content);
    closeModal();
  };

  const handleEditorChange = (newContent: string) => {
    setCurrentContent(newContent);
  };

  return (
    <HalfScreenModal
      width={900}
      title="Edit Large Text Block"
      visible={isOpenModal}
      onCancel={handleCancel}
      onSubmit={handleSubmit}
      confirmModalShow={true}
      afterClose={() => {
        textEditorRef?.current?.resetUndo();
        textEditorRef?.current?.clearSelection();
      }}
    >
      <div className={styles.textEditorWrapper}>
        <RichTextEditor
          limit={10000}
          initialValue={currentContent}
          onChange={handleEditorChange}
          defaultText={currentContent}
          ref={textEditorRef}
          masterId={benguideId}
          activateDefaultValue={false}
          editorKey={editorKey}
        />
      </div>
    </HalfScreenModal>
  );
};

TextEditor.propTypes = {
  isOpenModal: PropTypes.bool.isRequired,
  closeModal: PropTypes.func.isRequired,
  content: PropTypes.string.isRequired,
  benguideId: PropTypes.string.isRequired,
  onSave: PropTypes.func.isRequired,
  editorKey: PropTypes.string.isRequired,
  customPageId: PropTypes.string.isRequired,
  customPageSectionId: PropTypes.string.isRequired,
  componentType: PropTypes.oneOf(['rich_media', 'content_card']),
  title: PropTypes.string,
};

export default TextEditor;
